import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import TextAlign from '@tiptap/extension-text-align'
import { TextStyle } from '@tiptap/extension-text-style'
import FontFamily from '@tiptap/extension-font-family'

// Custom FontSize extension for proper font size handling
const FontSize = TextStyle.extend({
  addAttributes() {
    return {
      fontSize: {
        default: null,
        parseHTML: element => {
          const size = element.style.fontSize
          return size || null
        },
        renderHTML: attrs => {
          if (!attrs.fontSize) return {}
          // Normalize to px
          const value = String(attrs.fontSize).endsWith('px')
            ? attrs.fontSize
            : `${attrs.fontSize}px`
          return { style: `font-size: ${value}` }
        },
      },
    }
  },
})

import { Button } from './ui/button'
import { 
  Bold, 
  Italic, 
  Underline, 
  List, 
  ListOrdered, 
  Quote, 
  Heading1, 
  Heading2, 
  Heading3,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo,
  Redo,
  Type
} from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { cn } from '@/lib/utils'
import { useCallback, useState, useEffect } from 'react'

const FONT_SIZES = ['11', '12', '14', '16', '18', '24'] // numeric display, we will write px

const toPx = (n: string) => `${n}px`

function getSelectionFontSize(editor: any) {
  if (!editor) return null

  const { from, to } = editor.state.selection

  // Caret only -> read mark at cursor
  if (from === to) {
    const { fontSize } = editor.getAttributes('textStyle') || {}
    return fontSize ? fontSize.replace('px', '') : null
  }

  // Range -> scan text nodes and collect sizes
  const sizes = new Set()
  editor.state.doc.nodesBetween(from, to, (node: any) => {
    if (node.isText) {
      const mark = node.marks.find((m: any) => m.type.name === 'textStyle')
      const s = mark?.attrs?.fontSize
      if (s) sizes.add(String(s).replace('px', ''))
    }
  })

  if (sizes.size === 0) return null
  if (sizes.size === 1) return Array.from(sizes)[0]
  return 'Mixed'
}

function setFontSize(editor: any, value: string) {
  if (!editor) return
  
  // If value is empty or "Default", unset the mark
  if (!value || value === 'Default') {
    editor.chain().focus().unsetMark('textStyle').run()
    return
  }

  editor.chain().focus()
    .setMark('textStyle', { fontSize: toPx(value) })
    .run()
}

interface RichTextEditorProps {
  content: string
  onChange: (content: string) => void
  placeholder?: string
  className?: string
}

export function RichTextEditor({ content, onChange, placeholder, className }: RichTextEditorProps) {
  const [currentFontSize, setCurrentFontSize] = useState('')

  const editor = useEditor({
    extensions: [
      StarterKit,
      FontSize,
      FontFamily,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline cursor-pointer',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    onSelectionUpdate: () => {
      // Update font size when selection changes
      const size = getSelectionFontSize(editor)
      setCurrentFontSize(size || 'Default')
    },
    onFocus: () => {
      // Update font size when editor gains focus
      const size = getSelectionFontSize(editor)
      setCurrentFontSize(size || 'Default')
    },
    editorProps: {
      attributes: {
        class: 'focus:outline-none min-h-[200px] p-4',
      },
    },
  })

  // Update editor content when prop changes
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content)
    }
  }, [editor, content])

  // Update font size when editor changes
  useEffect(() => {
    if (!editor) return
    const update = () => {
      const size = getSelectionFontSize(editor)
      setCurrentFontSize(size || 'Default')
    }
    update()
    // Update on selection change and content update
    editor.on('selectionUpdate', update)
    editor.on('update', update)
    return () => { 
      editor.off('selectionUpdate', update)
      editor.off('update', update)
    }
  }, [editor])

  const handleFontSizeChange = useCallback((value: string) => {
    if (!editor) return
    
    if (value === 'Default' || value === 'Mixed') {
      setFontSize(editor, 'Default')
    } else {
      setFontSize(editor, value) // v is "12" -> we write 12px
    }
    
    // Update current font size immediately
    setTimeout(() => {
      const size = getSelectionFontSize(editor)
      setCurrentFontSize(size || 'Default')
    }, 0)
  }, [editor])

  const addLink = useCallback(() => {
    const url = window.prompt('Enter URL:')
    if (url && editor) {
      editor.chain().focus().setLink({ href: url }).run()
    }
  }, [editor])

  const addImage = useCallback(() => {
    const url = window.prompt('Enter image URL:')
    if (url && editor) {
      editor.chain().focus().setImage({ src: url }).run()
    }
  }, [editor])

  // Dropdown label logic
  const displayValue = 
    currentFontSize === 'Mixed' ? 'Mixed'
    : currentFontSize === 'Default' || !currentFontSize ? 'Default'
    : currentFontSize // numeric string like "12"

  if (!editor) {
    return null
  }

  return (
    <div className={cn("border", className)}>
      {/* Sticky Toolbar */}
      <div className="sticky top-0 z-[100] bg-background/95 backdrop-blur-sm border-b p-3 flex flex-wrap gap-1 shadow-sm">
        {/* Font Size */}
        <Select
          value={displayValue}
          onValueChange={handleFontSizeChange}
        >
          <SelectTrigger className="w-20 h-8">
            <Type className="h-4 w-4 mr-1" />
            <SelectValue>{displayValue}</SelectValue>
          </SelectTrigger>
          <SelectContent className="z-[110] bg-background border shadow-md">
            {FONT_SIZES.map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
            <SelectItem value="Default">Default</SelectItem>
          </SelectContent>
        </Select>

        <div className="w-px h-6 bg-border mx-1" />

        {/* Text Formatting */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={editor.isActive('strike') ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}
        >
          <Underline className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />

        {/* Headings */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={editor.isActive('heading', { level: 1 }) ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}
        >
          <Heading1 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={editor.isActive('heading', { level: 2 }) ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}
        >
          <Heading2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={editor.isActive('heading', { level: 3 }) ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}
        >
          <Heading3 className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />

        {/* Lists */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={editor.isActive('blockquote') ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}
        >
          <Quote className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />

        {/* Alignment */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={editor.isActive({ textAlign: 'left' }) ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={editor.isActive({ textAlign: 'center' }) ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={editor.isActive({ textAlign: 'right' }) ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}
        >
          <AlignRight className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />

        {/* Links and Images */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addLink}
          className="hover:bg-accent/50"
        >
          <LinkIcon className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addImage}
          className="hover:bg-accent/50"
        >
          <ImageIcon className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />

        {/* Undo/Redo */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className="hover:bg-accent/50 disabled:opacity-50"
        >
          <Undo className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className="hover:bg-accent/50 disabled:opacity-50"
        >
          <Redo className="h-4 w-4" />
        </Button>
      </div>

      {/* Editor Content */}
      <div className="relative">
        <EditorContent 
          editor={editor} 
          className="article-content min-h-[200px] mt-2 text-foreground"
        />
        
        {/* Placeholder */}
        {!content && placeholder && (
          <div className="absolute top-6 left-8 text-muted-foreground pointer-events-none">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  )
}